import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import AddressForm from '../components/AddressForm';
import PageHeader from '../components/PageHeader';
import DeleteAddressModal from '../components/DeleteAddressModal';
import BulkDeleteModal from '../components/BulkDeleteModal';
import AddressToolbar, { type SortOption, type ShipmentFilter } from '../components/addresses/AddressToolbar';
import BulkActionsBar from '../components/addresses/BulkActionsBar';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { addressesApi } from '../api/client';
import type { Address, AddressRecord } from '../types';

type AddressPage = Awaited<ReturnType<typeof addressesApi.listPage>>;
import { releaseInFlightKey, tryAcquireInFlightKey } from '../utils/inFlightKeys';
import { hasPreparedBulkRecoveryState } from '../utils/retryApiIdentity';

const emptyAddress: Address = {
  name: '',
  company: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  phone: '',
  country: 'US',
  gift: '',
  tshirt_size: '',
};


function AddressBookPage() {
  const { toasts, removeToast, success, error, warning } = useToast();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const addressesQuery = useInfiniteQuery({
    queryKey: ['addresses'],
    queryFn: ({ pageParam }) => addressesApi.listPage({ limit: 200, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });

  // Flatten the cursor pages into a de-duplicated (by id) list.
  const addresses = useMemo(() => {
    const byId = new Map<string, AddressRecord>();
    for (const page of addressesQuery.data?.pages ?? []) {
      for (const address of page.addresses) byId.set(address.id, address);
    }
    return [...byId.values()];
  }, [addressesQuery.data]);
  const loading = addressesQuery.isLoading;
  const loadingMore = addressesQuery.isFetchingNextPage;

  // Optimistically transform every cached address page (create/update/delete)
  // so the list updates without a refetch, matching the previous local-state UX.
  const updateAddressCache = (transform: (addrs: AddressRecord[]) => AddressRecord[]) => {
    queryClient.setQueryData<InfiniteData<AddressPage>>(['addresses'], (data) =>
      data ? { ...data, pages: data.pages.map((p) => ({ ...p, addresses: transform(p.addresses) })) } : data
    );
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [shipmentFilter, setShipmentFilter] = useState<ShipmentFilter>('all');

  // Form state
  const [formAddress, setFormAddress] = useState<Address>(emptyAddress);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const saveInFlightRef = useRef(false);
  const saveGenerationRef = useRef(0);

  // Delete modal state
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Mark shipped state
  const [markingShippedIds, setMarkingShippedIds] = useState<Set<string>>(new Set());
  const markingShippedIdsRef = useRef(new Set<string>());

  // Sync from orders state
  const [syncing, setSyncing] = useState(false);
  const [addressSyncCursor, setAddressSyncCursor] = useState<string | null>(null);

  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    return () => {
      saveGenerationRef.current += 1;
      saveInFlightRef.current = false;
    };
  }, []);

  // Surface the initial load failure as a toast, once per distinct failure.
  // Uses isLoadingError (first fetch, no data) — not isError — so a refetch
  // failure triggered from a mutation handler doesn't double-toast on top of
  // that handler's own error message.
  useEffect(() => {
    if (addressesQuery.isLoadingError) {
      const message =
        addressesQuery.error instanceof Error ? addressesQuery.error.message : 'Failed to load addresses';
      error('Load Failed', message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressesQuery.error, addressesQuery.isLoadingError]);

  useEffect(() => {
    if (addressesQuery.isFetchNextPageError) {
      error('Load More Failed', 'Failed to load more addresses.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressesQuery.isFetchNextPageError]);

  // Reload all pages; returns false if the reload itself failed.
  const reloadAddresses = async (): Promise<boolean> => {
    const result = await addressesQuery.refetch();
    return !result.isError;
  };

  const handleLoadMoreAddresses = async () => {
    if (!addressesQuery.hasNextPage || addressesQuery.isFetchingNextPage) return;
    await addressesQuery.fetchNextPage();
  };

  const filteredAddresses = useMemo(() => {
    let result = addresses;

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (addr) =>
          addr.name?.toLowerCase().includes(query) ||
          addr.company?.toLowerCase().includes(query) ||
          addr.city?.toLowerCase().includes(query) ||
          addr.state?.toLowerCase().includes(query) ||
          addr.email?.toLowerCase().includes(query)
      );
    }

    // Filter by shipment status
    if (shipmentFilter === 'shipped') {
      result = result.filter((addr) => (addr.shipment_stats?.count || 0) > 0);
    } else if (shipmentFilter === 'not_shipped') {
      result = result.filter((addr) => (addr.shipment_stats?.count || 0) === 0);
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'city':
          return (a.city || '').localeCompare(b.city || '');
        case 'shipments':
          return (b.shipment_stats?.count || 0) - (a.shipment_stats?.count || 0);
        case 'recent':
          const aDate = a.shipment_stats?.last_shipped_at || a.created_at || '';
          const bDate = b.shipment_stats?.last_shipped_at || b.created_at || '';
          return bDate.localeCompare(aDate);
        default:
          return 0;
      }
    });

    return result;
  }, [addresses, searchQuery, sortBy, shipmentFilter]);
  const visibleAddressIds = useMemo(
    () => new Set(filteredAddresses.map((address) => address.id)),
    [filteredAddresses]
  );
  const selectedVisibleAddresses = useMemo(
    () => filteredAddresses.filter((address) => selectedIds.has(address.id)),
    [filteredAddresses, selectedIds]
  );

  // Keep selection scoped to rows the user can currently see and act on.
  useEffect(() => {
    setSelectedIds((previous) => {
      const next = new Set(Array.from(previous).filter((id) => visibleAddressIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleAddressIds]);

  const handleEdit = (address: AddressRecord) => {
    if (saveInFlightRef.current) return;
    // Close add form when editing
    setShowAddForm(false);
    // Toggle accordion - if clicking same address, close it
    if (editingId === address.id) {
      setEditingId(null);
      setFormAddress(emptyAddress);
      return;
    }
    setEditingId(address.id);
    setFormAddress({
      name: address.name || '',
      company: address.company || '',
      street1: address.street1 || '',
      street2: address.street2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      email: address.email || '',
      phone: address.phone || '',
      country: address.country || 'US',
      gift: address.gift || '',
      tshirt_size: address.tshirt_size || '',
    });
  };

  const handleCancel = () => {
    if (saveInFlightRef.current) return;
    const previouslyEditingId = editingId;
    const wasAdding = showAddForm;
    setEditingId(null);
    setShowAddForm(false);
    setFormAddress(emptyAddress);
    if (previouslyEditingId) {
      window.requestAnimationFrame(() => {
        document.getElementById(`edit-address-toggle-${previouslyEditingId}`)?.focus();
      });
    } else if (wasAdding) {
      window.requestAnimationFrame(() => {
        document.getElementById('add-address-toggle')?.focus();
      });
    }
  };

  const handleAddNew = () => {
    if (saveInFlightRef.current) return;
    setEditingId(null);
    setFormAddress(emptyAddress);
    setShowAddForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saveInFlightRef.current) return;
    const submittedEditingId = editingId;
    const submittedAddress = { ...formAddress };

    if (!submittedAddress.name || !submittedAddress.street1 || !submittedAddress.city || !submittedAddress.state || !submittedAddress.zip) {
      error('Validation Error', 'Please fill in all required fields.');
      return;
    }

    if (submittedAddress.gift === 'Tshirt' && !submittedAddress.tshirt_size) {
      error('Validation Error', 'Select a T-shirt size.');
      return;
    }

    saveInFlightRef.current = true;
    const saveGeneration = ++saveGenerationRef.current;
    setSaving(true);

    try {
      if (submittedEditingId) {
        const updated = await addressesApi.update(submittedEditingId, submittedAddress);
        if (saveGeneration !== saveGenerationRef.current) return;
        updateAddressCache((addrs) =>
          addrs.map((addr) =>
            addr.id === submittedEditingId
              ? {
                  ...updated,
                  shipment_stats: updated.shipment_stats ?? addr.shipment_stats,
                }
              : addr
          )
        );
        success('Address Updated', 'The address has been updated successfully.');
      } else {
        const created = await addressesApi.create(submittedAddress);
        if (saveGeneration !== saveGenerationRef.current) return;
        // Append to the last loaded page so it lands at the end of the flat list.
        // If the list never loaded (empty/errored cache), seed a page so the new
        // address is still shown immediately, matching the old local-state UX.
        queryClient.setQueryData<InfiniteData<AddressPage>>(['addresses'], (data) => {
          if (!data || data.pages.length === 0) {
            return { pages: [{ addresses: [created], next_cursor: null }], pageParams: [undefined] };
          }
          const pages = data.pages.slice();
          const last = pages[pages.length - 1];
          pages[pages.length - 1] = { ...last, addresses: [...last.addresses, created] };
          return { ...data, pages };
        });
        success('Address Created', 'The address has been saved successfully.');
      }

      setEditingId(null);
      setShowAddForm(false);
      setFormAddress(emptyAddress);
      if (submittedEditingId) {
        window.requestAnimationFrame(() => {
          document.getElementById(`edit-address-toggle-${submittedEditingId}`)?.focus();
        });
      } else {
        window.requestAnimationFrame(() => {
          document.getElementById('add-address-toggle')?.focus();
        });
      }
    } catch (err) {
      if (saveGeneration !== saveGenerationRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to save address';
      error('Save Failed', message);
    } finally {
      if (saveGeneration === saveGenerationRef.current) {
        saveInFlightRef.current = false;
        setSaving(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteId || saveInFlightRef.current) return;

    setDeleting(true);

    try {
      await addressesApi.delete(deleteId);
      updateAddressCache((addrs) => addrs.filter((addr) => addr.id !== deleteId));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteId);
        return next;
      });
      success('Address Deleted', 'The address has been removed.');
      setDeleteId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete address';
      error('Delete Failed', message);
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkShipped = async (addr: AddressRecord) => {
    if (
      saveInFlightRef.current ||
      !tryAcquireInFlightKey(markingShippedIdsRef.current, addr.id)
    ) return;
    setMarkingShippedIds(new Set(markingShippedIdsRef.current));

    try {
      await addressesApi.markShipped(addr.id);
      success('Marked as Shipped', `${addr.name} has been marked as shipped.`);
      // Reload addresses to get updated shipment stats
      await reloadAddresses();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark as shipped';
      error('Mark Shipped Failed', message);
    } finally {
      releaseInFlightKey(markingShippedIdsRef.current, addr.id);
      setMarkingShippedIds(new Set(markingShippedIdsRef.current));
    }
  };

  const handleSyncFromOrders = async () => {
    setSyncing(true);

    try {
      const result = await addressesApi.syncFromOrders({
        limit: 100,
        cursor: addressSyncCursor || undefined,
      });
      setAddressSyncCursor(result.next_cursor);
      if (result.created > 0) {
        success(
          result.list_complete ? 'Sync Complete' : 'Sync Page Complete',
          `Imported ${result.created} address${result.created !== 1 ? 'es' : ''} from this bounded page.`
        );
        await reloadAddresses();
      } else if (!result.list_complete) {
        warning(
          'More History Available',
          `Processed ${result.processed} records. Select Continue Sync to process the next page.`
        );
      } else {
        success('Sync Complete', 'No new addresses found in shipment history.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync addresses';
      error('Sync Failed', message);
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (saveInFlightRef.current) return;
    if (checked) {
      setSelectedIds(new Set(filteredAddresses.map((addr) => addr.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (saveInFlightRef.current) return;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked && visibleAddressIds.has(id)) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const selectedVisibleCount = selectedVisibleAddresses.length;
  const isAllSelected = filteredAddresses.length > 0 && selectedVisibleCount === filteredAddresses.length;
  const isSomeSelected = selectedVisibleCount > 0;

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (saveInFlightRef.current) return;
    const idsToDelete = selectedVisibleAddresses.map((address) => address.id);
    if (idsToDelete.length === 0) return;
    const idSet = new Set(idsToDelete);

    setBulkDeleting(true);

    try {
      const result = await addressesApi.bulkDelete(idsToDelete);
      if (!result.success || result.failed > 0) {
        const refreshed = await reloadAddresses();
        warning(
          'Some Deletes Failed',
          `${result.deleted} confirmed deleted; ${result.failed} not confirmed.${result.error ? ` ${result.error}.` : ''} ${refreshed ? 'The list has been refreshed.' : 'Reload the page to reconcile the list.'}`
        );
      } else {
        updateAddressCache((addrs) => addrs.filter((addr) => !idSet.has(addr.id)));
        success('Addresses Deleted', `Successfully deleted ${result.deleted} address${result.deleted !== 1 ? 'es' : ''}.`);
      }
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete addresses';
      const refreshed = await reloadAddresses();
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
      error(
        'Bulk Delete Failed',
        `${message}. ${refreshed ? 'The list has been refreshed.' : 'Reload the page to reconcile the list.'}`
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk order handler - navigate to bulk ship page with selected addresses
  const handleBulkOrder = () => {
    if (selectedVisibleAddresses.length === 0 || saveInFlightRef.current) return;

    // Store selected address IDs in sessionStorage for the bulk ship page
    const selectedAddresses = selectedVisibleAddresses;
    try {
      const existingQueue = sessionStorage.getItem('bulkShipAddresses');
      if (existingQueue) {
        try {
          const parsed = JSON.parse(existingQueue);
          const hasPreparedRecovery = hasPreparedBulkRecoveryState(parsed);
          if (hasPreparedRecovery) {
            warning(
              'Prepared Shipments Need Attention',
              'A previous bulk queue must be retried or discarded before starting a new one.'
            );
            navigate('/bulk-ship');
            return;
          }
        } catch {
          // A malformed stale value can be safely replaced by the new selection.
        }
      }
      sessionStorage.setItem('bulkShipAddresses', JSON.stringify(selectedAddresses));
      navigate('/bulk-ship');
    } catch {
      error('Bulk Queue Unavailable', 'Browser session storage is unavailable. No shipments were started.');
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <PageHeader
        title="Address book"
        description="Saved recipients with shipment statistics · select rows to bulk ship"
        actions={
          <button
            id="add-address-toggle"
            type="button"
            className="btn btn-primary"
            aria-expanded={showAddForm}
            aria-controls={showAddForm ? 'add-address-form-panel' : undefined}
            onClick={() => (showAddForm ? handleCancel() : handleAddNew())}
            disabled={saving}
          >
            <Plus size={14} />
            Add address
          </button>
        }
      />

      {showAddForm && (
        <div className="card" id="add-address-form-panel" style={{ marginBottom: '16px' }}>
          <div className="card-head">
            <span className="card-title">New address</span>
          </div>
          <form onSubmit={handleSubmit} aria-busy={saving}>
            <div className="card-body">
              <AddressForm
                title="Add New Address"
                idPrefix="add-addr"
                address={formAddress}
                onChange={setFormAddress}
                disabled={saving}
                hideHeader
              />
            </div>
            <div className="card-foot">
              <button
                type="submit"
                className={`btn btn-primary ${saving ? 'loading' : ''}`}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save address'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Address list card */}
      <div className="card">
        <AddressToolbar
          syncing={syncing}
          syncCursor={addressSyncCursor}
          onSync={handleSyncFromOrders}
          searchQuery={searchQuery}
          onSearchChange={(value) => {
            setSearchQuery(value);
            clearSelection();
          }}
          shipmentFilter={shipmentFilter}
          onShipmentFilterChange={(value) => {
            setShipmentFilter(value);
            clearSelection();
          }}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />

          {isSomeSelected && (
            <BulkActionsBar
              count={selectedVisibleCount}
              disabled={saving}
              onCreateShipments={handleBulkOrder}
              onDelete={() => setShowBulkDeleteModal(true)}
              onClear={clearSelection}
            />
          )}

          {loading ? (
            <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              <div className="skeleton" style={{ height: '44px' }} />
              <div className="skeleton" style={{ height: '44px' }} />
              <div className="skeleton" style={{ height: '44px' }} />
            </div>
          ) : filteredAddresses.length === 0 ? (
            <div className="empty-state">
              <p>
                {searchQuery
                  ? 'No addresses match your search.'
                  : 'No saved addresses yet. Add one above!'}
              </p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        title="Select all"
                        aria-label="Select all visible addresses"
                        disabled={saving}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>City, State</th>
                    <th>Gift</th>
                    <th style={{ textAlign: 'right' }}>Shipments</th>
                    <th>Last Shipped</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAddresses.map((addr) => (
                    <React.Fragment key={addr.id}>
                      <tr className={editingId === addr.id ? 'editing-row' : ''}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(addr.id)}
                            onChange={(e) => handleSelectOne(addr.id, e.target.checked)}
                            aria-label={`Select address for ${addr.name || 'unnamed recipient'}`}
                            disabled={saving}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td data-label="Name">
                          <strong>{addr.name}</strong>
                          {addr.company && (
                            <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)' }}>
                              {addr.company}
                            </div>
                          )}
                        </td>
                        <td data-label="Address">
                          {addr.street1}
                          {addr.street2 && `, ${addr.street2}`}
                        </td>
                        <td data-label="City, State">
                          {addr.city}, {addr.state} {addr.zip}
                          {addr.country && addr.country !== 'US' ? ` ${addr.country}` : ''}
                        </td>
                        <td data-label="Gift">
                          {addr.gift
                            ? addr.gift === 'Tshirt' && addr.tshirt_size
                              ? `${addr.gift} (${addr.tshirt_size})`
                              : addr.gift
                            : '-'}
                        </td>
                        <td data-label="Shipments" style={{ textAlign: 'right' }}>
                          <span className="count-pill">
                            {addr.shipment_stats?.count || 0}
                          </span>
                        </td>
                        <td data-label="Last Shipped" className="td-mono td-muted" style={{ fontSize: '0.72rem' }}>
                          {formatDate(addr.shipment_stats?.last_shipped_at)}
                        </td>
                        <td data-label="Actions" style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {(addr.shipment_stats?.count || 0) === 0 && (
                              <button
                                className={`btn btn-secondary btn-small ${markingShippedIds.has(addr.id) ? 'loading' : ''}`}
                                onClick={() => handleMarkShipped(addr)}
                                disabled={saving || markingShippedIds.has(addr.id)}
                                title="Mark as shipped"
                              >
                                {markingShippedIds.has(addr.id) ? '…' : 'Shipped'}
                              </button>
                            )}
                            <button
                              id={`edit-address-toggle-${addr.id}`}
                              className={`btn ${editingId === addr.id ? 'btn-primary' : 'btn-secondary'} btn-small`}
                              onClick={() => handleEdit(addr)}
                              disabled={saving}
                              aria-expanded={editingId === addr.id}
                              aria-controls={`edit-address-panel-${addr.id}`}
                              title={editingId === addr.id ? 'Close edit' : 'Edit address'}
                            >
                              {editingId === addr.id ? 'Close' : 'Edit'}
                            </button>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => setDeleteId(addr.id)}
                              disabled={saving}
                              title="Delete address"
                              aria-label="Delete address"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Accordion Edit Row */}
                      <tr className={`accordion-edit-row ${editingId === addr.id ? 'open' : ''}`}>
                        <td colSpan={8} style={{ padding: 0, border: 'none' }}>
                          <div
                            id={`edit-address-panel-${addr.id}`}
                            className={`accordion-edit-content ${editingId === addr.id ? 'open' : ''}`}
                            aria-hidden={editingId !== addr.id}
                          >
                            <form onSubmit={handleSubmit} style={{ padding: '16px' }} aria-busy={saving}>
                              <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '14px' }}>
                                Edit address
                              </div>
                              <AddressForm
                                title="Edit Address"
                                idPrefix={`edit-addr-${addr.id}`}
                                address={formAddress}
                                onChange={setFormAddress}
                                disabled={saving}
                                hideHeader
                              />

                              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                                <button
                                  type="submit"
                                  className={`btn btn-primary ${saving ? 'loading' : ''}`}
                                  disabled={saving}
                                >
                                  {saving ? 'Saving…' : 'Update address'}
                                </button>
                                <button type="button" className="btn btn-ghost" onClick={handleCancel} disabled={saving}>
                                  Cancel
                                </button>
                              </div>
                            </form>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="card-foot-bar">
            <span className="count">
              {loading
                ? ' '
                : `${filteredAddresses.length} address${filteredAddresses.length !== 1 ? 'es' : ''}`}
            </span>
            {addressesQuery.hasNextPage && !loading && (
              <button
                type="button"
                className={`btn btn-secondary btn-small ${loadingMore ? 'loading' : ''}`}
                onClick={handleLoadMoreAddresses}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
      </div>

      <DeleteAddressModal
        isOpen={!!deleteId}
        deleting={deleting}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />

      <BulkDeleteModal
        isOpen={showBulkDeleteModal}
        count={selectedVisibleCount}
        deleting={bulkDeleting}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
      />
    </>
  );
}

export default AddressBookPage;
